/* RESPONSIBLE TEAM: team-automation-foundations */
import { fragment } from 'ember-data-model-fragments/attributes';
import { buildValidations, validator } from 'ember-cp-validations';
import Step from 'embercom/models/operator/visual-builder/step';
import type Store from '@ember-data/store';
import { TargetContext } from 'embercom/objects/operator/configuration/custom-bot';
import { SUPPORTED_CHANNELS } from 'embercom/lib/operator/custom-bots/constants-2';
import { PRIORITY_LEVELS } from 'embercom/lib/inbox/priority-levels';

// Reusing the `rule-action-is-valid` validator (used by sibling steps like
// `assign-conversation` and `set-ticket-state`) so that the optional level
// picker can mark the step invalid via the action fragment.
//
// No `presence` validator here — legacy steps persisted before this PR may have
// no embedded action fragment. `rule-action-is-valid` already returns `true`
// when the value is null/falsy, so those steps remain valid and can still save.
const Validations = buildValidations({
  action: [
    validator('rule-action-is-valid', {
      message: 'operator.workflows.visual-builder.validations.empty-action-error',
    }),
  ],
});

export default class ChangeConversationPriority extends Step.extend(Validations) {
  // Surfaces the embedded `action` JSON the backend always serializes for this
  // step. Previously the Ember model ignored it because the step was binary
  // (URGENT only); now the level picker writes to `action.actionData.priority`,
  // which the macro-style splat dispatcher (Issue 4-followup) reads as the
  // priority_level integer. Same fragment type as `assign-conversation`.
  @fragment('rules/rule-action') declare action: any;

  get supportedChannels() {
    return SUPPORTED_CHANNELS.changeConversationPriority;
  }

  static get supportedContexts() {
    return [
      TargetContext.Conversation,
      TargetContext.CustomerTicket,
      TargetContext.BackOfficeTicket,
      TargetContext.TrackerTicket,
    ];
  }

  static get requiredBillingFeature() {
    return 'inbox_priority_rules';
  }

  static createNewStep(store: Store): ChangeConversationPriority {
    return store.createRecord('operator/visual-builder/step/change-conversation-priority', {
      action: store.createFragment('rules/rule-action', {
        type: 'change-conversation-priority',
        justAdded: true,
        // Default both keys so the legacy binary code paths (which read
        // `priority`) and the new multi-level path (which reads
        // `priority_level`) resolve to URGENT for fresh steps. Aligns with
        // `rule-action-service.js#addActionToTarget`.
        actionData: {
          priority: PRIORITY_LEVELS.PRIORITY,
          priority_level: PRIORITY_LEVELS.PRIORITY,
        },
      }),
      type: 'operator/visual-builder/step/change-conversation-priority',
    });
  }
}
